import React, { useEffect, useState } from "react";
import {
  Button,
  Dropdown,
  Grid,
  Icon,
  Input,
  Label,
  Modal,
} from "semantic-ui-react";
import styled from "styled-components";
import {
  fetchAllStreamsWithDetails,
  fetchDeviceFilterOptions,
  FetchStreamsAPIResponse,
} from "../../../../BytebeamClient";
import {
  AlertGroups,
  AlertRule,
  AlertRuleCriticality,
  CompositeCondition,
  Condition,
  dropDownOptionsFromArray,
  SimpleCondition,
  specialCharacterValidation,
} from "../../../../util";
import { MoreInfoPopUp } from "../../../common/ActionsUtils";
import { beamtoast } from "../../../common/CustomToast";
import SqlWhereClauseBuilder from "../../common/SqlWhereClauseBuilder";
import { ThinDivider } from "../../Dashboards/Panel/util";
import { DashboardsInfo } from "../../Dashboards/ViewDashboard";
import { ErrorMessage } from "../../Settings/roles/CreateOrEditRoleModal";
import { AlertRuleOperationType } from "./AlertRules";

type AlertRuleType = "signal_based_alert" | "no_signal_alert";

export const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

export const StyledLabel = styled(Label)`
  margin: 0px !important;
  min-width: 80px;
`;

export const StyledInput = styled(Input)`
  margin-bottom: 14px;
  height: 36px;
  width: 100%;
`;

const StyledAlertGroupRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
`;

type alertErrorsType = {
  error: boolean;
  message: string;
  field: "alert_group" | "alert_name";
};

const isAlertRuleNameValid = (
  alertRule: AlertRule,
  setAlertErrors: (error: alertErrorsType) => void
) => {
  if (alertRule?.name?.length > 64) {
    setAlertErrors({
      error: true,
      message: "Alert Name should not exceed 64 characters.",
      field: "alert_name",
    });
    return false;
  }
  return true;
};

const isAlertGroupNameValid = (
  alertGroupName: string,
  setAlertErrors: (error: alertErrorsType) => void
) => {
  if (alertGroupName?.length > 0) {
    if (alertGroupName?.length > 64) {
      setAlertErrors({
        error: true,
        message: "Alert Group Name should not exceed 64 characters.",
        field: "alert_group",
      });
      return false;
    } else if (!specialCharacterValidation(alertGroupName)) {
      setAlertErrors({
        error: true,
        message:
          "Alert Group Name should begin with an alphanumeric character and only contain alphanumeric, space, and underscores.",
        field: "alert_group",
      });
      return false;
    }
  }

  setAlertErrors({
    error: false,
    message: "",
    field: "alert_group",
  });
  return true;
};

type AlertGroupInputProps = {
  readonly alertGroupsOptions: { key: string; text: string; value: string }[];
  readonly alertRule: AlertRule;
  readonly setAlertGroup: (group: string) => void;
  readonly alertErrors: alertErrorsType;
};

function AlertGroupInput({
  alertGroupsOptions,
  alertRule,
  setAlertGroup,
  alertErrors,
}: AlertGroupInputProps) {
  const [isAlertGroupNameInputShown, setIsAlertGroupNameInputShown] =
    useState(false);
  const [addAlertGroupInputValue, setAddAlertGroupInputValue] = useState("");
  const [alertGroupDropdownValue, setAlertGroupDropdownValue] = useState(
    alertRule.group
  );

  return (
    <>
      <StyledAlertGroupRow>
        <StyledInput labelPosition="left" style={{ marginBottom: "0px" }}>
          <StyledLabel>Alert Group</StyledLabel>
          <Dropdown
            fluid
            selection
            search
            disabled={isAlertGroupNameInputShown}
            placeholder="Select Alert Group Name"
            options={alertGroupsOptions}
            value={
              isAlertGroupNameInputShown ? "" : alertRule.group || "default"
            }
            onChange={(_e, d) => {
              setAlertGroup(d.value as string);
            }}
            style={{
              border: "none",
            }}
          />
        </StyledInput>
        {!isAlertGroupNameInputShown && (
          <>
            <b>OR</b>
            <Button
              title="Add New Alert Group"
              id="addAlertGroupButton"
              icon
              secondary
              labelPosition="left"
              onClick={() => {
                setAlertGroup("");
                setAlertGroupDropdownValue(alertRule.group);
                setIsAlertGroupNameInputShown(true);
              }}
              style={{ border: "1px solid #a7a9b7" }}
            >
              <Icon name="plus" />
              <span style={{ whiteSpace: "nowrap" }}>Alert Group</span>
            </Button>
          </>
        )}
      </StyledAlertGroupRow>
      {isAlertGroupNameInputShown && (
        <>
          <StyledAlertGroupRow>
            <StyledInput labelPosition="left" style={{ marginBottom: "0px" }}>
              <StyledLabel>Add Alert Group</StyledLabel>
              <input
                placeholder="Enter new alert group name..."
                type="text"
                autoFocus
                defaultValue={""}
                value={addAlertGroupInputValue}
                onChange={(e) => {
                  setAlertGroup(e.target.value);
                  setAddAlertGroupInputValue(e.target.value);
                }}
              />
            </StyledInput>
            <Button
              title="Cancel, Select from existing alert groups"
              id="removeAlertGroupButton"
              icon="minus"
              secondary
              onClick={() => {
                setAlertGroup(alertGroupDropdownValue);
                setIsAlertGroupNameInputShown(false);
              }}
            />
          </StyledAlertGroupRow>
          {alertErrors.error && alertErrors.field === "alert_group" ? (
            <ErrorMessage id={"Alert Group Name error span"}>
              {alertErrors.message}
            </ErrorMessage>
          ) : (
            ""
          )}
        </>
      )}
    </>
  );
}

type CreateSignalBasedAlertRuleProps = {
  readonly alertRule: AlertRule;
  readonly setAlertRule: React.Dispatch<React.SetStateAction<AlertRule>>;
  readonly setIsValid: (isValid: boolean) => void;
  readonly streams: FetchStreamsAPIResponse;
  readonly alertGroupsOptions: { key: string; text: string; value: string }[];
  readonly deviceIdOptions: { key: string; text: string; value: number }[];
  readonly operationType: string;
};

function CreateSignalBasedAlertRule(props: CreateSignalBasedAlertRuleProps) {
  const {
    streams,
    setIsValid,
    alertRule,
    setAlertRule,
    alertGroupsOptions,
    deviceIdOptions,
  } = props;

  const fields = streams?.[alertRule?.stream]?.fields ?? {};

  const [alertErrors, setAlertErrors] = useState<alertErrorsType>({
    error: false,
    message: "",
    field: "alert_group",
  });

  const setDeviceFilter = (deviceIds: number[]) => {
    setAlertRule((prevAlertRule) => {
      if (!prevAlertRule) return prevAlertRule;

      return {
        ...prevAlertRule,
        device_filter: {
          ...(deviceIds?.length > 0 ? { id: deviceIds } : {}),
        } as AlertRule["device_filter"],
      };
    });
  };

  const setInputStream = (inputStream: string) => {
    setAlertRule((prevAlertRule) => {
      if (!prevAlertRule) return prevAlertRule;
      return {
        ...prevAlertRule,
        stream: inputStream,
        condition: {
          conditions: [{ field: "", operator: "=", value: "" }],
          operator: "and",
        } as CompositeCondition,
      };
    });
  };

  const setAlertName = (alertName: string) => {
    setAlertRule((prevAlertRule) => {
      if (!prevAlertRule) return prevAlertRule;
      return {
        ...prevAlertRule,
        name: alertName,
      };
    });
  };

  const setAlertCondition = (condition: Condition) => {
    setAlertRule((prevAlertRule) => {
      if (!prevAlertRule) return prevAlertRule;
      return {
        ...prevAlertRule,
        condition: condition,
      };
    });
  };

  const setAlertGroup = (group: string) => {
    setAlertRule((prevAlertRule) => {
      if (!prevAlertRule) return prevAlertRule;
      return {
        ...prevAlertRule,
        group: group.trim(),
      };
    });
  };

  const setActivationThreshold = (threshold: string) => {
    setAlertRule((prevAlertRule) => {
      if (!prevAlertRule) return prevAlertRule;
      return {
        ...prevAlertRule,
        activation_threshold_seconds: parseFloat(threshold),
      };
    });
  };

  const setDeactivationThreshold = (threshold: string) => {
    setAlertRule((prevAlertRule) => {
      if (!prevAlertRule) return prevAlertRule;
      return {
        ...prevAlertRule,
        deactivation_threshold_seconds: parseFloat(threshold),
      };
    });
  };

  const streamOptions = dropDownOptionsFromArray(Object.keys(streams));

  useEffect(() => {
    const isValidCondition = (condition: Condition) => {
      const compositeOperators = ["and", "or"];

      if (compositeOperators.includes(condition.operator)) {
        const conditions = (condition as CompositeCondition).conditions;
        return conditions.every(isValidCondition);
      } else {
        const simpleCondition = condition as SimpleCondition;
        return (
          simpleCondition.field?.length > 0 &&
          simpleCondition.value !== undefined &&
          simpleCondition.value !== ""
        );
      }
    };

    const isValidSignalBasedAlert = (alertRule: AlertRule) => {
      return (
        alertRule.name?.length > 0 &&
        alertRule.activation_threshold_seconds > 0 &&
        alertRule.deactivation_threshold_seconds >= 0 &&
        alertRule.stream?.length > 0 &&
        alertRule.group?.length > 0 &&
        isValidCondition(alertRule.condition)
      );
    };

    setIsValid(
      isAlertRuleNameValid(alertRule, setAlertErrors) &&
        isAlertGroupNameValid(alertRule.group, setAlertErrors) &&
        isValidSignalBasedAlert(alertRule)
    );
  }, [alertRule, setIsValid]);

  return (
    <Grid>
      <Grid.Row style={{ paddingBottom: 0 }}>
        <Grid.Column width={8}>
          <StyledInput labelPosition="left">
            <StyledLabel>Alert Name</StyledLabel>
            <input
              placeholder="Alert Name"
              value={alertRule.name}
              onChange={(e) => setAlertName(e.target.value)}
            />
          </StyledInput>
          {alertErrors.error && alertErrors.field === "alert_name" ? (
            <ErrorMessage id={"Alert Name error span"}>
              {alertErrors.message}
            </ErrorMessage>
          ) : (
            ""
          )}
        </Grid.Column>
        <Grid.Column width={8}>
          <StyledInput labelPosition="left">
            <StyledLabel>Stream</StyledLabel>
            <Dropdown
              fluid
              selection
              search
              disabled={props.operationType === AlertRuleOperationType.Update}
              placeholder="Stream"
              options={streamOptions}
              value={alertRule.stream}
              onChange={(_e, d) => {
                setInputStream(d.value as string);
              }}
              style={{
                border: "none",
              }}
            />
          </StyledInput>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row style={{ paddingTop: 0 }}>
        <Grid.Column width={16}>
          <StyledInput labelPosition="left">
            <StyledLabel>Device Filter (IDs)</StyledLabel>
            <Dropdown
              key={`device-filter-${alertRule.id}`}
              fluid
              selection
              search
              multiple
              placeholder="Select Device IDs"
              options={deviceIdOptions || []}
              defaultValue={alertRule.device_filter?.id || []}
              onChange={(_e, d) => {
                setDeviceFilter(d.value as number[]);
              }}
              style={{
                border: "none",
              }}
            />
          </StyledInput>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row style={{ padding: 0 }}>
        <Grid.Column width={16}>
          <SqlWhereClauseBuilder
            fields={fields}
            condition={alertRule.condition}
            onChange={setAlertCondition}
            supportNullOperators={false}
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row style={{ paddingBottom: 0 }}>
        <Grid.Column width={16}>
          <AlertGroupInput
            alertGroupsOptions={alertGroupsOptions}
            alertRule={alertRule}
            setAlertGroup={setAlertGroup}
            alertErrors={alertErrors}
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row style={{ paddingTop: 0 }}>
        <Grid.Column width={16}>
          <StyledInput labelPosition="left">
            <StyledLabel>Activation Threshold (seconds)</StyledLabel>
            <input
              placeholder="Activation Threshold (seconds)"
              type="number"
              value={alertRule.activation_threshold_seconds}
              onChange={(e) => setActivationThreshold(e.target.value)}
            />
          </StyledInput>
        </Grid.Column>

        <Grid.Column width={16}>
          <StyledInput labelPosition="left">
            <StyledLabel>Deactivation Threshold (seconds)</StyledLabel>
            <input
              placeholder="Deactivation Threshold (seconds)"
              type="number"
              value={alertRule.deactivation_threshold_seconds}
              onChange={(e) => setDeactivationThreshold(e.target.value)}
            />
          </StyledInput>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
}

type CreateNoSignalAlertRuleProps = {
  readonly alertRule: AlertRule;
  readonly setAlertRule: React.Dispatch<React.SetStateAction<AlertRule>>;
  readonly setIsValid: (isValid: boolean) => void;
  readonly streams: FetchStreamsAPIResponse;
  readonly alertGroupsOptions: { key: string; text: string; value: string }[];
  readonly deviceIdOptions: { key: string; text: string; value: number }[];
  readonly operationType: string;
};

function CreateNoSignalAlertRule(props: CreateNoSignalAlertRuleProps) {
  const {
    streams,
    setIsValid,
    alertRule,
    setAlertRule,
    alertGroupsOptions,
    deviceIdOptions,
  } = props;
  const [alertErrors, setAlertErrors] = useState<alertErrorsType>({
    error: false,
    message: "",
    field: "alert_group",
  });

  useEffect(() => {
    const isValidNoSignalAlert = (alertRule: AlertRule) => {
      return (
        alertRule.name?.length > 0 &&
        alertRule.activation_threshold_seconds > 0 &&
        alertRule.stream?.length > 0 &&
        alertRule.group?.length > 0
      );
    };

    setIsValid(
      isAlertRuleNameValid(alertRule, setAlertErrors) &&
        isAlertGroupNameValid(alertRule.group, setAlertErrors) &&
        isValidNoSignalAlert(alertRule)
    );
  }, [alertRule, setIsValid]);

  const setInputStream = (inputStream: string) => {
    setAlertRule({
      ...alertRule,
      stream: inputStream,
    });
  };

  const setAlertName = (alertName: string) => {
    setAlertRule({
      ...alertRule,
      name: alertName,
    });
  };

  const setAlertThreshold = (threshold: string) => {
    setAlertRule({
      ...alertRule,
      activation_threshold_seconds: parseFloat(threshold) * 60,
    });
  };

  const setAlertGroup = (group: string) => {
    setAlertRule({
      ...alertRule,
      group: group.trim(),
    });
  };

  const setDeviceFilter = (deviceIds: number[]) => {
    setAlertRule((prevAlertRule) => {
      if (!prevAlertRule) return prevAlertRule;

      return {
        ...prevAlertRule,
        device_filter: {
          ...(deviceIds?.length > 0 ? { id: deviceIds } : {}),
        } as AlertRule["device_filter"],
      };
    });
  };

  const streamOptions = dropDownOptionsFromArray(Object.keys(streams));
  return (
    <Grid>
      <Grid.Row>
        <Grid.Column width={8}>
          <StyledInput labelPosition="left">
            <StyledLabel>Alert Name</StyledLabel>
            <input
              placeholder="Alert Name"
              value={alertRule.name}
              onChange={(e) => setAlertName(e.target.value)}
            />
          </StyledInput>
          {alertErrors.error && alertErrors.field === "alert_name" ? (
            <ErrorMessage id={"Alert Name error span"}>
              {alertErrors.message}
            </ErrorMessage>
          ) : (
            ""
          )}
        </Grid.Column>
        <Grid.Column width={8}>
          <StyledInput labelPosition="left">
            <StyledLabel>Stream</StyledLabel>
            <Dropdown
              fluid
              selection
              search
              disabled={props.operationType === AlertRuleOperationType.Update}
              placeholder="Stream"
              options={streamOptions}
              value={alertRule.stream}
              onChange={(_e, d) => {
                setInputStream(d.value as string);
              }}
              style={{
                border: "none",
              }}
            />
          </StyledInput>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row style={{ paddingTop: 0 }}>
        <Grid.Column width={16}>
          <StyledInput labelPosition="left">
            <StyledLabel>Device Filter (IDs)</StyledLabel>
            <Dropdown
              key={`device-filter-${alertRule.id}`}
              fluid
              selection
              search
              multiple
              placeholder="Select Device IDs"
              options={deviceIdOptions || []}
              defaultValue={alertRule.device_filter?.id || []}
              onChange={(_e, d) => {
                setDeviceFilter(d.value as number[]);
              }}
              style={{
                border: "none",
              }}
            />
          </StyledInput>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row style={{ padding: 0 }}>
        <Grid.Column width={16}>
          <AlertGroupInput
            alertGroupsOptions={alertGroupsOptions}
            alertRule={alertRule}
            setAlertGroup={setAlertGroup}
            alertErrors={alertErrors}
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row style={{ paddingTop: 0 }}>
        <Grid.Column width={16}>
          <StyledInput labelPosition="left">
            <StyledLabel>Alert Threshold (minutes)</StyledLabel>
            <input
              placeholder="Alert Threshold (minutes)"
              type="number"
              value={parseFloat(
                (alertRule.activation_threshold_seconds / 60).toFixed(3)
              )}
              onChange={(e) => setAlertThreshold(e.target.value)}
            />
          </StyledInput>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
}

interface CreateAlertRuleModalProps {
  readonly open: boolean;
  readonly onOpen: () => void;
  readonly onClose: () => void;
  readonly alertRule?: AlertRule;
  readonly alertRules: AlertRule[];
  readonly alertGroups: AlertGroups;
  readonly title: string;
  readonly onSubmit: (alertRule: AlertRule) => void;
  readonly alertModalStep: number;
  readonly operationType: AlertRuleOperationType;
  readonly dashboards: DashboardsInfo[];
}

export default function CreateAlertRuleModal(props: CreateAlertRuleModalProps) {
  const {
    open,
    onOpen,
    onClose,
    alertRules,
    title,
    onSubmit,
    operationType,
    alertGroups,
    dashboards,
  } = props;

  function getAlertRuleSchema(alertType: AlertRuleType): AlertRule {
    let alertRule: AlertRule = {
      id: "",
      name: "",
      stream: "",
      condition: {
        operator: "and",
        conditions: [{ operator: "=", field: "", value: "" }],
      },
      group: "default",
      activation_threshold_seconds: 30,
      deactivation_threshold_seconds: 0,
      criticality: "critical",
      metadata_filters: {},
      device_filter: {},
      deepdive_dashboard_id: "",
    };

    if (alertType === "no_signal_alert") {
      alertRule = {
        id: "",
        name: "",
        stream: "",
        criticality: "critical",
        group: "default",
        metadata_filters: {},
        activation_threshold_seconds: 10 * 60,
        deactivation_threshold_seconds: 0,
        condition: {
          operator: "no_data",
        },
        device_filter: {},
        deepdive_dashboard_id: "",
      };
    }

    return alertRule;
  }

  const [selectedAlertType, setSelectedAlertType] =
    useState<AlertRuleType>("signal_based_alert");
  const [streams, setStreams] = useState<FetchStreamsAPIResponse>({});
  const [alertRule, setAlertRule] = useState<AlertRule>(
    props.alertRule ?? getAlertRuleSchema("signal_based_alert")
  );
  const [allAlertNames, setAllAlertNames] = useState<Set<string>>(new Set());
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [deviceIdOptions, setDeviceIdOptions] = useState<
    { key: string; text: string; value: number }[]
  >([]);

  // Create a new array including "default" if it's not already in alertGroups
  const extendedAlertGroups = alertGroups.includes("default")
    ? alertGroups
    : [...alertGroups, "default"];

  // Generate dropdown options from the new array
  const alertGroupsOptions = dropDownOptionsFromArray(extendedAlertGroups);

  useEffect(() => {
    const fn = async () => {
      const streams = await fetchAllStreamsWithDetails();
      setStreams(streams);
    };

    fn();
  }, []);

  useEffect(() => {
    const fetchDeviceIds = async () => {
      try {
        const options = await fetchDeviceFilterOptions({});
        const idOption = options.find((opt) => opt.filterName === "id");

        if (idOption) {
          setDeviceIdOptions(
            idOption.filterValues.map((id) => ({
              key: id,
              text: id,
              value: parseInt(id),
            }))
          );
        }
      } catch (e) {
        console.log("Error fetching device IDs:", e);
      }
    };

    fetchDeviceIds();
  }, []);

  const setIsValid = (isValid: boolean) => {
    setButtonDisabled(!isValid);
  };

  const setAlertType = (alertType: AlertRuleType) => {
    const alertRule = getAlertRuleSchema(alertType);
    setAlertRule(alertRule);
  };

  const renderContent = () => {
    return (
      <>
        <Grid.Row>
          <Grid.Column width={8}>
            <StyledInput labelPosition="left">
              <StyledLabel>
                Choose Alert Type{" "}
                <MoreInfoPopUp
                  wide
                  trigger={
                    <Icon
                      name="question circle outline"
                      style={{ cursor: "pointer", marginLeft: "4px" }}
                    />
                  }
                  content={
                    selectedAlertType === "signal_based_alert"
                      ? "Signal Based Alerts are triggered when a device sends a specific signal or data that meets predefined criteria."
                      : "No Signal Based Alerts are triggered by the absence of expected signals or data within a certain time frame."
                  }
                  offset={[0, 5]}
                  inverted
                  position="top center"
                />
              </StyledLabel>
              <Dropdown
                fluid
                selection
                search
                placeholder="Choose Alert Type"
                options={[
                  {
                    key: "signal_based_alert",
                    text: "Signal Alert",
                    value: "signal_based_alert",
                  },
                  {
                    key: "no_signal_alert",
                    text: "No Signal Alert",
                    value: "no_signal_alert",
                  },
                ]}
                value={selectedAlertType}
                onChange={(_e, d) => {
                  setSelectedAlertType(d.value as AlertRuleType);
                  setAlertType(d.value as AlertRuleType);
                }}
                style={{ border: "none" }}
                disabled={operationType === AlertRuleOperationType.Update}
              />
            </StyledInput>
          </Grid.Column>
        </Grid.Row>

        <ThinDivider />

        {/* Render Alert Form based on selection */}

        {selectedAlertType === "no_signal_alert" ? (
          <CreateNoSignalAlertRule
            alertRule={alertRule}
            setAlertRule={setAlertRule}
            setIsValid={setIsValid}
            streams={streams}
            alertGroupsOptions={alertGroupsOptions}
            operationType={operationType}
            deviceIdOptions={deviceIdOptions}
          />
        ) : (
          <CreateSignalBasedAlertRule
            alertRule={alertRule}
            setAlertRule={setAlertRule}
            setIsValid={setIsValid}
            streams={streams}
            alertGroupsOptions={alertGroupsOptions}
            operationType={operationType}
            deviceIdOptions={deviceIdOptions}
          />
        )}
      </>
    );
  };

  const alertCriticalityOptions = dropDownOptionsFromArray([
    "critical",
    "warning",
    "info",
  ]);

  const setAlertCriticality = (criticality: AlertRuleCriticality) => {
    setAlertRule((prevAlertRule) => {
      if (!prevAlertRule) return prevAlertRule;
      return {
        ...prevAlertRule,
        criticality: criticality,
      };
    });
  };

  const setAlertDashboardId = (deepdive_dashboard_ids: string) => {
    setAlertRule((prevAlertRule) => {
      if (!prevAlertRule) return prevAlertRule;
      return {
        ...prevAlertRule,
        deepdive_dashboard_id: deepdive_dashboard_ids,
      };
    });
  };

  const renderAlertCriticality = () => {
    return (
      <StyledInput labelPosition="left">
        <StyledLabel>Alert Criticality</StyledLabel>

        <Dropdown
          selection
          fluid
          search
          placeholder="Alert Criticality"
          options={alertCriticalityOptions}
          value={alertRule?.criticality}
          onChange={(_e, d) => {
            setAlertCriticality(d.value as AlertRuleCriticality);
          }}
          style={{
            border: "none",
          }}
        />
      </StyledInput>
    );
  };

  const dashboardLinks = dashboards.map((dashboard) => {
    return {
      key: `${dashboard.id}`,
      text: `${dashboard.id} - ${dashboard.title} (${dashboard.type})`,
      value: `${dashboard.id}`,
    };
  });

  const handleSubmitAlertRule = () => {
    if (allAlertNames.has(alertRule.name.toLowerCase())) {
      beamtoast.error("Alert name already exists");
    } else {
      onClose();
      onSubmit(alertRule);
    }
  };

  const renderDashboardLinks = () => {
    return (
      <StyledInput labelPosition="left">
        <StyledLabel>Add Dashboard Links</StyledLabel>

        <Dropdown
          selection
          fluid
          search
          clearable
          placeholder="Select Dashboard Id"
          options={dashboardLinks}
          value={alertRule?.deepdive_dashboard_id}
          onChange={(_e, d) => {
            setAlertDashboardId(d.value as string);
          }}
          style={{
            border: "none",
          }}
        />
      </StyledInput>
    );
  };

  useEffect(() => {
    if (props.alertRule && Object.keys(props.alertRule).length > 0) {
      setAlertRule(props.alertRule);
      setSelectedAlertType(
        props.alertRule.condition.operator === "no_data"
          ? "no_signal_alert"
          : "signal_based_alert"
      );
    } else if (operationType === AlertRuleOperationType.Create) {
      setSelectedAlertType("signal_based_alert");
      setAlertType("signal_based_alert");
    }
  }, [props.alertRule, operationType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const currentAlertName = (props.alertRule?.name ?? "").toLowerCase();

    const alertNamesSet = new Set(
      alertRules.flatMap((alertRule) =>
        alertRule.name.toLowerCase() !== currentAlertName
          ? [alertRule.name.toLowerCase()]
          : []
      )
    );

    setAllAlertNames(alertNamesSet);
  }, [alertRules, props.alertRule]);

  return (
    <Modal
      className="dark"
      onClose={onClose}
      onOpen={onOpen}
      open={open}
      size={"small"}
    >
      <Modal.Header>{title}</Modal.Header>
      <Modal.Content>
        {renderContent()}
        {renderAlertCriticality()}
        {renderDashboardLinks()}
      </Modal.Content>
      <Modal.Actions>
        <Button
          secondary
          onClick={() => {
            setAlertRule(getAlertRuleSchema("signal_based_alert"));
            setSelectedAlertType("signal_based_alert");
            onClose();
          }}
        >
          Cancel
        </Button>

        <Button
          type="submit"
          primary
          disabled={buttonDisabled}
          onClick={() => {
            handleSubmitAlertRule();
          }}
        >
          Submit
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
